// frontend/src/context/DepartmentContext.js
// The single "scope" control for the app: which DEPARTMENT we're looking at.
// The program is DERIVED from the department (department → program), so there is
// no separate program selector — picking a department dictates the program and
// everything downstream (cycles, KPIs, role types, evaluations, nav items).
//
// - Everyone (employee / manager / dept_head) defaults to their OWN department
//   on login and sees only that department's data.
// - Admins (and dept heads) can switch via the sidebar selector; "All
//   departments" is null. The choice is persisted and drives every API call —
//   the axios interceptor injects ?department_id (and the derived ?program_id)
//   on GETs.
// - Switching reloads the page so every loaded view refetches with the new scope.
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { getDepartments, setActiveDepartmentInApi } from '../services/api';

const DepartmentContext = createContext(null);
const STORAGE_KEY = 'epro_active_department';

export function DepartmentProvider({ children }) {
  const { user } = useAuth();
  const [activeDepartmentId, setActiveDepartmentIdState] = useState(null);
  const [departments, setDepartments] = useState([]);

  const canSwitch = !!user && (user.role === 'admin' || user.role === 'dept_head');

  // The active program follows the active department. "All departments" (or a
  // user without one) falls back to the caller's own program. Used for nav
  // gating; API correctness comes from the backend deriving program from the
  // injected ?department_id, so a brief pre-load fallback here is harmless.
  const activeProgramId = (() => {
    if (activeDepartmentId != null) {
      const d = departments.find(x => Number(x.id) === Number(activeDepartmentId));
      if (d) return Number(d.program_id);
    }
    return Number(user?.program_id) || 1;
  })();

  // Load the department list for those who can switch — needed for the selector
  // and to derive the program from the selected department.
  useEffect(() => {
    if (!canSwitch) { setDepartments([]); return; }
    getDepartments().then(d => setDepartments(Array.isArray(d) ? d : [])).catch(() => setDepartments([]));
  }, [canSwitch]);

  // Initialise the active department once the user is known.
  useEffect(() => {
    if (!user) { setActiveDepartmentInApi(null); return; }
    let initial;
    if (user.role === 'admin') {
      // '' (stored) means the admin explicitly chose "All departments".
      const stored = localStorage.getItem(STORAGE_KEY);
      initial = (stored === null || stored === '') ? null : Number(stored);
    } else if (user.role === 'dept_head') {
      // Fresh login (no stored choice — cleared on logout) → their OWN department.
      // Otherwise honor their in-session switch ('' = they chose "All departments").
      const stored = localStorage.getItem(STORAGE_KEY);
      initial = stored === null ? (Number(user.department_id) || null) : (stored === '' ? null : Number(stored));
    } else {
      // Employees & managers are locked to their own department.
      initial = Number(user.department_id) || null;
    }
    setActiveDepartmentIdState(initial);
    setActiveDepartmentInApi(initial);
  }, [user]);

  // id === null => "All departments"
  const setActiveDepartmentId = (id) => {
    const n = id === '' || id == null ? null : Number(id);
    if (n === activeDepartmentId) return;
    localStorage.setItem(STORAGE_KEY, n === null ? '' : String(n));
    setActiveDepartmentInApi(n);
    window.location.reload();
  };

  return (
    <DepartmentContext.Provider value={{ activeDepartmentId, setActiveDepartmentId, activeProgramId, departments }}>
      {children}
    </DepartmentContext.Provider>
  );
}

export const useActiveDepartment = () =>
  useContext(DepartmentContext) || { activeDepartmentId: null, setActiveDepartmentId: () => {}, activeProgramId: 1, departments: [] };
